/**
 * Root layout for unity/base React Router v7 SPA.
 * Replaces Next.js src/app/layout.tsx.
 * ADR-006, ADR-020, E32 T-3900.
 */

import { I18nextProvider } from 'react-i18next'
import { Outlet } from 'react-router-dom'
import { ApolloProvider } from '@apollo/client/react'
import { ThemeProvider, useTheme } from 'next-themes'
import { useEffect } from 'react'
import { CookieBanner } from '@unity/cookie-consent'
import i18n from '@/lib/i18n'
import { apolloClient } from '@/lib/apollo'

function ThemeWatcher() {
  const { resolvedTheme, setTheme } = useTheme()
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    function onMediaChange() {
      const systemTheme = media.matches ? 'dark' : 'light'
      if (resolvedTheme === systemTheme) setTheme('system')
    }
    onMediaChange()
    media.addEventListener('change', onMediaChange)
    return () => media.removeEventListener('change', onMediaChange)
  }, [resolvedTheme, setTheme])
  return null
}

function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <I18nextProvider i18n={i18n}>
      <ApolloProvider client={apolloClient}>
        <ThemeProvider attribute="class" disableTransitionOnChange>
          <ThemeWatcher />
          {children}
          <CookieBanner />
        </ThemeProvider>
      </ApolloProvider>
    </I18nextProvider>
  )
}

export default function Root() {
  return (
    <AppProviders>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:text-gray-900 focus:shadow-lg focus:outline-2 focus:outline-offset-2 focus:outline-blue-600"
      >
        Skip to main content
      </a>
      <Outlet />
    </AppProviders>
  )
}
